import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api';

// ==================== Initial State ====================
const initialState = {
  incidentReports: [],
  currentIncidentReport: null,
  isLoading: false,
  error: null,
};

// ==================== Async Thunks ====================

// Fetch all Incident Reports (optionally by patient ID)
export const fetchIncidentReports = createAsyncThunk(
  'incidentReports/fetchIncidentReports',
  async (patientId = null, { rejectWithValue }) => {
    try {
      // If your backend supports filtering by patient, add that here:
      const url = patientId
        ? `/incident-reports/patient/${patientId}`
        : '/incident-reports';
      const response = await api.get(url);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch incident reports'
      );
    }
  }
);

// Fetch a single Incident Report
export const fetchIncidentReport = createAsyncThunk(
  'incidentReports/fetchIncidentReport',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/incident-reports/${id}`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch incident report'
      );
    }
  }
);

// Create a new Incident Report
export const createIncidentReport = createAsyncThunk(
  'incidentReports/createIncidentReport',
  async (incidentData, { rejectWithValue }) => {
    try {
      const response = await api.post('/incident-reports', incidentData);
      return response.data.data;
    } catch (error) {
      if (error.response?.status === 422) {
        return rejectWithValue(error.response.data.errors);
      }
      return rejectWithValue(
        error.response?.data?.error || 'Failed to create incident report'
      );
    }
  }
);

// Update an existing Incident Report
export const updateIncidentReport = createAsyncThunk(
  'incidentReports/updateIncidentReport',
  async ({ case_id, incidentData }, { rejectWithValue }) => {
    const payload = {
      ...incidentData,
      // patient_id: incidentData.patientId,
      case_id: incidentData.caseId,
    };
    // delete payload.patientId;
    delete payload.caseId;
    try {
      const response = await api.put(`/incident-reports/case/${case_id}`, incidentData);
      return response.data.data ?? response.data;
    } catch (error) {
      if (error.response?.status === 422) {
        return rejectWithValue(error.response.data.errors);
      }
      return rejectWithValue(
        error.response?.data?.error || 'Failed to update incident report'
      );
    }
  }
);

// Delete an Incident Report
export const deleteIncidentReport = createAsyncThunk(
  'incidentReports/deleteIncidentReport',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/incident-reports/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to delete incident report'
      );
    }
  }
);

// ==================== Slice ====================
const incidentReportsSlice = createSlice({
  name: 'incidentReports',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentIncidentReport: (state, action) => {
      state.currentIncidentReport = action.payload;
    },
    clearCurrentIncidentReport: (state) => {
      state.currentIncidentReport = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // FETCH ALL
      .addCase(fetchIncidentReports.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchIncidentReports.fulfilled, (state, action) => {
        state.isLoading = false;
        state.incidentReports = action.payload;
      })
      .addCase(fetchIncidentReports.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // FETCH SINGLE
      .addCase(fetchIncidentReport.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchIncidentReport.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentIncidentReport = action.payload;
      })
      .addCase(fetchIncidentReport.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // CREATE
      .addCase(createIncidentReport.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createIncidentReport.fulfilled, (state, action) => {
        state.isLoading = false;
        state.incidentReports.unshift(action.payload);
      })
      .addCase(createIncidentReport.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // UPDATE
      .addCase(updateIncidentReport.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateIncidentReport.fulfilled, (state, action) => {
        state.isLoading = false;
        const updated = action.payload.data ?? action.payload;

        const index = state.incidentReports.findIndex((d) => d.id === updated.id);
        if (index !== -1) {
          state.incidentReports[index] = updated;
        }

        if (state.currentIncidentReport?.id === updated.id) {
          state.currentIncidentReport = updated;
        }
      })
      .addCase(updateIncidentReport.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // DELETE
      .addCase(deleteIncidentReport.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteIncidentReport.fulfilled, (state, action) => {
        state.isLoading = false;
        state.incidentReports = state.incidentReports.filter(
          (d) => d.id !== action.payload
        );

        if (state.currentIncidentReport?.id === action.payload) {
          state.currentIncidentReport = null;
        }
      })
      .addCase(deleteIncidentReport.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearError,
  setCurrentIncidentReport,
  clearCurrentIncidentReport,
} = incidentReportsSlice.actions;

export default incidentReportsSlice.reducer;
